import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, Image, Download, Settings, X } from 'lucide-react'
import toast from 'react-hot-toast'
import Dropdown from '../components/ui/Dropdown'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface ResizeOptions {
  method: 'pixels' | 'percentage'
  width: number
  height: number
  maintainAspectRatio: boolean
  quality: number
}

interface ProcessedFile {
  id: string
  originalFile: File
  resizedBlob?: Blob
  originalDimensions: { width: number; height: number }
  targetDimensions: { width: number; height: number }
  originalSize: number
  resizedSize?: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
}

// Image resize utility function
const resizeImage = async (file: File, options: ResizeOptions, originalDimensions: { width: number; height: number }): Promise<{ blob: Blob; dimensions: { width: number; height: number } }> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = document.createElement('img')
    
    img.onload = () => {
      let targetWidth: number
      let targetHeight: number
      
      if (options.method === 'percentage') {
        targetWidth = Math.round(originalDimensions.width * (options.width / 100))
        targetHeight = Math.round(originalDimensions.height * (options.height / 100))
      } else {
        targetWidth = options.width
        targetHeight = options.height
        
        if (options.maintainAspectRatio) {
          const aspectRatio = originalDimensions.width / originalDimensions.height
          if (targetWidth / targetHeight > aspectRatio) {
            targetWidth = Math.round(targetHeight * aspectRatio)
          } else {
            targetHeight = Math.round(targetWidth / aspectRatio)
          }
        }
      }
      
      canvas.width = targetWidth
      canvas.height = targetHeight
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }
      
      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve({
            blob,
            dimensions: { width: targetWidth, height: targetHeight }
          })
        } else {
          reject(new Error('Resize failed'))
        }
      }, file.type, options.quality / 100)
    }
    
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(file)
  })
}

// Get image dimensions
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img')
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(file)
  })
}

const ImageResizer: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ResizeOptions>({
    method: 'pixels',
    width: 800,
    height: 600,
    maintainAspectRatio: true,
    quality: 90
  })

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const imageFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/')
    )

    if (imageFiles.length === 0) {
      toast.error('Please select valid image files')
      return
    }

    const newFiles: ProcessedFile[] = []
    
    for (const file of imageFiles) {
      try {
        const dimensions = await getImageDimensions(file)
        const targetDimensions = options.method === 'percentage' 
          ? {
              width: Math.round(dimensions.width * (options.width / 100)),
              height: Math.round(dimensions.height * (options.height / 100))
            }
          : { width: options.width, height: options.height }
        
        newFiles.push({
          id: Math.random().toString(36).substr(2, 9),
          originalFile: file,
          originalDimensions: dimensions,
          targetDimensions,
          originalSize: file.size,
          status: 'pending',
          progress: 0
        })
      } catch (error) {
        toast.error(`Failed to process ${file.name}`)
      }
    }

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${newFiles.length} image(s) for resizing`)
  }, [options])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif']
    },
    multiple: true
  })

  const processFiles = async () => {
    const pendingFiles = files.filter(file => file.status === 'pending')
    
    for (const file of pendingFiles) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 20 } : f
        ))

        const result = await resizeImage(file.originalFile, options, file.originalDimensions)
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { 
            ...f, 
            resizedBlob: result.blob,
            targetDimensions: result.dimensions,
            resizedSize: result.blob.size,
            status: 'completed',
            progress: 100
          } : f
        ))

        toast.success(`Resized ${file.originalFile.name}`)
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to resize ${file.originalFile.name}`)
      }
    }
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.resizedBlob) return

    const url = URL.createObjectURL(file.resizedBlob)
    const a = document.createElement('a')
    a.href = url
    const extension = file.originalFile.name.split('.').pop()
    a.download = `${file.originalFile.name.split('.')[0]}_resized.${extension}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const updateDimensions = (field: 'width' | 'height', value: number) => {
    if (options.maintainAspectRatio && options.method === 'percentage') {
      setOptions(prev => ({
        ...prev,
        width: value,
        height: value
      }))
    } else {
      setOptions(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <Image className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            Smart Image Resizer
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Resize your images with precision while maintaining perfect quality and aspect ratios.
          </p>
        </div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageResizer.header}
          className="mb-8"
        />

        {/* Options Panel */}
        <div className="card mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Settings className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Resize Options</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resize Method
              </label>
              <Dropdown
                options={[
                  {
                    value: 'pixels',
                    label: 'Pixels',
                    description: 'Set exact width and height in pixels'
                  },
                  {
                    value: 'percentage',
                    label: 'Percentage',
                    description: 'Scale by percentage of original size'
                  }
                ]}
                value={options.method}
                onChange={(value) => setOptions(prev => ({ ...prev, method: value as 'pixels' | 'percentage' }))}
                placeholder="Select resize method"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quality: {options.quality}%
              </label>
              <input
                type="range"
                min="10"
                max="100"
                value={options.quality}
                onChange={(e) => setOptions(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #8438ff 0%, #8438ff ${options.quality}%, #e5e7eb ${options.quality}%, #e5e7eb 100%)`
                }}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {options.method === 'pixels' ? 'Width (px)' : 'Scale (%)'}
              </label>
              <input
                type="number"
                value={options.width}
                onChange={(e) => updateDimensions('width', parseInt(e.target.value) || 0)}
                min={options.method === 'pixels' ? 1 : 1}
                max={options.method === 'pixels' ? 10000 : 500}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
              />
            </div>
            
            {options.method === 'pixels' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Height (px)
                </label>
                <input
                  type="number"
                  value={options.height}
                  onChange={(e) => updateDimensions('height', parseInt(e.target.value) || 0)}
                  min="1"
                  max="10000"
                  disabled={options.maintainAspectRatio}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            )}
            
            <div className="flex items-end">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={options.maintainAspectRatio}
                  onChange={(e) => setOptions(prev => ({ ...prev, maintainAspectRatio: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-gray-700">Maintain aspect ratio</span>
              </label>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div
          {...getRootProps()}
          className={`card border-2 border-dashed cursor-pointer transition-colors ${
            isDragActive ? 'border-[#8438ff] bg-[#8438ff]/10' : 'border-gray-300 hover:border-[#8438ff]'
          }`}
        >
          <input {...getInputProps()} />
          <div className="text-center py-12">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-xl font-medium text-[#8438ff] mb-2">
              {isDragActive ? 'Drop images here' : 'Choose images or drag them here'}
            </p>
            <p className="text-gray-600">
              Supports JPG, PNG, WebP, and GIF files
            </p>
          </div>
        </div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageResizer.content}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#8438ff]">Files to Process</h3>
              <button
                onClick={processFiles}
                disabled={!files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Resize All Images
              </button>
            </div>
            
            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Image className="h-8 w-8 text-gray-400" />
                      <div>
                        <p className="font-medium text-[#8438ff]">{file.originalFile.name}</p>
                        <p className="text-sm text-gray-600">
                          {file.originalDimensions.width}×{file.originalDimensions.height} → {file.targetDimensions.width}×{file.targetDimensions.height} | {formatFileSize(file.originalSize)}
                          {file.resizedSize && ` → ${formatFileSize(file.resizedSize)}`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {file.status === 'processing' && (
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-[#8438ff] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      )}
                      
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageResizer.footer}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default ImageResizer
